// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Breadcrumbs, JsonLD } from '../../controls/breadcrumbs';
import { useLocale } from '../../hooks/locale';
import { translatePropertyMainType } from '../translate/propertyTypes';
import { listingsDescriptor } from '../AggregatesListings/listingsDescriptor';
import { useAggregatesListingH1 } from './AggregatesListingH1';
import { makeListingHref } from './listing-href';
import type { AggregatesListingBreadcrumbs_listing$key } from './__generated__/AggregatesListingBreadcrumbs_listing.graphql';

type Props = {|
  listing: AggregatesListingBreadcrumbs_listing$key,
|};

const listingsHref = params => ({
  pathname: '/listings',
  query: listingsDescriptor.getQueryParams(params),
});

const PROPERTY_TYPES_WITHOUT_ROOMS = [
  'BUILDING',
  'COMMERCIAL',
  'GASTRO',
  'OTHER',
  'PARK',
  'PROP',
  'ROOM',
];

export const AggregatesListingBreadcrumbs = (props: Props): React.Node => {
  const { t, language } = useLocale();

  const listing = useFragment(
    graphql`
      fragment AggregatesListingBreadcrumbs_listing on AggregatesListing {
        slug
        offerType
        numberOfRooms
        compositePropertyType
        localityObject {
          slug
          lang
          breadcrumbs {
            slug
            lang
            label
            canonicalPlace {
              slug
            }
          }
        }

        ...AggregatesListingH1_listing
      }
    `,
    props.listing,
  );

  const h1Title = useAggregatesListingH1(listing);

  const place = listing.localityObject;

  const { propertyTypeSingularCapital } = translatePropertyMainType(
    t,
    listing.compositePropertyType,
  );

  const numberOfRooms =
    listing.numberOfRooms == null ? null : Math.floor(listing.numberOfRooms);

  const items = [
    { label: '', isHome: true, href: { pathname: '/index', query: {} } },
    {
      label: listing.offerType === 'buy' ? t('buy') : t('rentVerb'),
      href: listingsHref({ offerType: listing.offerType }),
    },
    {
      label: propertyTypeSingularCapital,
      href: listingsHref({
        offerType: listing.offerType,
        compositePropertyType_eq: listing.compositePropertyType,
      }),
    },
    ...(place?.breadcrumbs
      .map(pl => ({
        label: pl.label,
        href: listingsHref({
          offerType: listing.offerType,
          compositePropertyType_eq: listing.compositePropertyType,
          placeSlugs: [
            {
              slug: pl.slug,
              lang: pl.lang,
            },
          ],
        }),
        rel: pl.canonicalPlace != null ? 'nofollow' : undefined,
      }))
      .reverse() ?? []),
    numberOfRooms == null ||
    PROPERTY_TYPES_WITHOUT_ROOMS.includes(listing.compositePropertyType)
      ? null
      : {
          label: t('roomsSingleSelected', { count: numberOfRooms }),
          href: listingsHref({
            offerType: listing.offerType,
            compositePropertyType_eq: listing.compositePropertyType,
            numberOfRooms_gte: numberOfRooms,
            numberOfRooms_lte: numberOfRooms + 0.5,
            placeSlugs:
              place != null
                ? [
                    {
                      slug: place.slug,
                      lang: language,
                    },
                  ]
                : null,
          }),
        },
    {
      label: h1Title,
      href: makeListingHref({
        offerType: listing.offerType,
        compositePropertyType: listing.compositePropertyType,
        slug: listing.slug,
      }),
    },
  ].filter(Boolean);

  return (
    <>
      <JsonLD items={items} />
      <Breadcrumbs items={items} />
    </>
  );
};
